import React, { useState } from 'react';
import '../styles/DocumentList.css';

const STATE_INFO = {
  ready:           { color: '#10b981', label: 'Ready' },
  in_progress:     { color: '#f59e0b', label: 'Writing…' },
  needs_attention: { color: '#ef4444', label: 'Needs attention' },
};

function getStateInfo(state) {
  return STATE_INFO[state] || { color: '#64748b', label: state || 'Unknown' };
}

/* ── Category groups ─────────────────────────────────────────────────────── */

const CATEGORY_ORDER = ['legal', 'marketing', 'social', 'ops'];
const CATEGORY_LABELS = {
  legal: 'Legal',
  marketing: 'Marketing',
  social: 'Social Media',
  ops: 'Business',
};

function groupByCategory(documents) {
  const groups = {};
  CATEGORY_ORDER.forEach(cat => { groups[cat] = []; });
  (documents || []).forEach(doc => {
    const cat = doc.category || 'ops';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(doc);
  });
  return groups;
}

/* ── Collapsible category section ─────────────────────────────────────────── */

function CategorySection({ label, documents, selectedId, onSelect }) {
  const [open, setOpen] = useState(true);
  if (!documents || documents.length === 0) return null;

  return (
    <div className="doc-category-group">
      <button className="doc-section-header" onClick={() => setOpen(!open)}>
        <span className="doc-section-chevron">{open ? '▼' : '▶'}</span>
        <span className="doc-section-label">{label}</span>
        <span className="doc-section-count">{documents.length}</span>
      </button>
      {open && documents.map(doc => {
        const stateInfo = getStateInfo(doc.state);
        return (
          <button
            key={doc.job_id}
            className={`doc-list-item ${doc.job_id === selectedId ? 'selected' : ''}`}
            onClick={() => onSelect(doc.job_id)}
          >
            <span
              className="doc-status-dot"
              style={{ backgroundColor: stateInfo.color }}
              title={stateInfo.label}
            />
            <span className="doc-list-info">
              <span className="doc-list-title">{doc.title}</span>
              <span className="doc-list-tag">{stateInfo.label}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */

export default function DocumentList({ documents, selectedId, onSelect, loading, error, onRetry }) {
  if (loading) {
    return (
      <div className="doc-list">
        <div className="doc-list-empty">Loading documents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doc-list">
        <div className="doc-list-empty">
          <p>Failed to load documents.</p>
          {onRetry && (
            <button className="doc-retry-btn" onClick={onRetry}>Try again</button>
          )}
        </div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="doc-list">
        <div className="doc-list-empty">
          <p>No documents yet</p>
          <p className="doc-list-hint">
            Start a conversation to order a privacy policy, bio, and more.
          </p>
        </div>
      </div>
    );
  }

  const grouped = groupByCategory(documents);

  return (
    <div className="doc-list">
      {CATEGORY_ORDER.map(cat => (
        <CategorySection
          key={cat}
          label={CATEGORY_LABELS[cat] || cat}
          documents={grouped[cat]}
          selectedId={selectedId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
